/**
 * 地形网格与地图生成。
 *
 * 生成完全由种子驱动，同一种子在任何机器上产生完全相同的地图。
 */

import { MAP_W, MAP_H } from '../../shared/constants.ts';
import { DetRandom } from '../fixed/rng.ts';

export const enum Tile {
  Ground = 0,
  Rock = 1,
  Ore = 2,
  Rift = 3,
}

export interface BaseSite {
  x: number;
  y: number;
}

export class Terrain {
  readonly tiles = new Uint8Array(MAP_W * MAP_H);
  /** 每格剩余矿量（SEU） */
  readonly ore = new Int32Array(MAP_W * MAP_H);
  /** 连通矿区编号，0 表示非矿。拾荒车锁定矿区后不再跨区折返 */
  readonly oreField = new Uint8Array(MAP_W * MAP_H);
  /** 建筑占用标记，寻路时视为不可通行 */
  readonly blocked = new Uint8Array(MAP_W * MAP_H);

  readonly bases: BaseSite[] = [];
  readonly rifts: BaseSite[] = [];

  idx(tx: number, ty: number): number {
    return ty * MAP_W + tx;
  }

  inBounds(tx: number, ty: number): boolean {
    return tx >= 0 && ty >= 0 && tx < MAP_W && ty < MAP_H;
  }

  /** 地形本身是否可通行（不含建筑占用） */
  passableTerrain(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return false;
    return this.tiles[this.idx(tx, ty)] !== Tile.Rock;
  }

  /** 综合可通行判定（含建筑） */
  passable(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return false;
    const i = this.idx(tx, ty);
    return this.tiles[i] !== Tile.Rock && this.blocked[i] === 0;
  }

  setBlocked(tx: number, ty: number, v: boolean): void {
    if (!this.inBounds(tx, ty)) return;
    this.blocked[this.idx(tx, ty)] = v ? 1 : 0;
  }

  oreAt(tx: number, ty: number): number {
    if (!this.inBounds(tx, ty)) return 0;
    return this.ore[this.idx(tx, ty)]!;
  }

  takeOre(tx: number, ty: number, amount: number): number {
    const i = this.idx(tx, ty);
    const have = this.ore[i]!;
    const got = have < amount ? have : amount;
    this.ore[i] = have - got;
    if (this.ore[i] === 0 && this.tiles[i] === Tile.Ore) this.tiles[i] = Tile.Ground;
    if (this.ore[i] === 0) this.oreField[i] = 0;
    return got;
  }

  generate(seed: number): void {
    const rng = new DetRandom(seed ^ 0x5eed);
    this.tiles.fill(Tile.Ground);
    this.ore.fill(0);
    this.oreField.fill(0);
    this.blocked.fill(0);
    this.bases.length = 0;
    this.rifts.length = 0;

    // 双方基地位置：对角布局，保证对称性与公平
    this.bases.push({ x: 15, y: 16 });
    this.bases.push({ x: MAP_W - 16, y: MAP_H - 17 });

    // ── 岩石群：用随机游走生成有机形状的障碍带 ──
    const clusters = 26;
    for (let c = 0; c < clusters; c++) {
      let cx = rng.range(6, MAP_W - 7);
      let cy = rng.range(6, MAP_H - 7);
      // 避开基地
      if (this.nearAnyBase(cx, cy, 14)) continue;
      const steps = rng.range(14, 46);
      for (let s = 0; s < steps; s++) {
        const r = rng.range(1, 2);
        this.stamp(cx, cy, r, Tile.Rock);
        cx += rng.range(-1, 1);
        cy += rng.range(-1, 1);
        if (cx < 3) cx = 3;
        if (cy < 3) cy = 3;
        if (cx > MAP_W - 4) cx = MAP_W - 4;
        if (cy > MAP_H - 4) cy = MAP_H - 4;
      }
    }

    // ── 矿点：每方 2 处主矿 + 中央 1 处富矿 ──
    const oreSites: Array<{ x: number; y: number; rich: boolean }> = [
      { x: 22, y: 13, rich: false },
      { x: 12, y: 25, rich: false },
      { x: MAP_W - 23, y: MAP_H - 14, rich: false },
      { x: MAP_W - 13, y: MAP_H - 26, rich: false },
      { x: MAP_W >> 1, y: MAP_H >> 1, rich: true },
      { x: 30, y: MAP_H - 24, rich: false },
      { x: MAP_W - 31, y: 24, rich: false },
    ];
    for (const s of oreSites) {
      const radius = s.rich ? 5 : 4;
      const perTile = s.rich ? 1800 : 1100;
      for (let dy = -radius; dy <= radius; dy++) {
        for (let dx = -radius; dx <= radius; dx++) {
          const d2 = dx * dx + dy * dy;
          if (d2 > radius * radius) continue;
          const tx = s.x + dx;
          const ty = s.y + dy;
          if (!this.inBounds(tx, ty)) continue;
          // 边缘稀疏化，形成自然的矿脉轮廓
          if (d2 > (radius - 1) * (radius - 1) && rng.chance(450)) continue;
          const i = this.idx(tx, ty);
          this.tiles[i] = Tile.Ore;
          this.ore[i] = perTile + rng.range(-200, 200);
        }
      }
    }
    this.labelOreFields();

    // ── ZLR 裂隙：中场两处，本原型中作为视觉地标与未来勘探点 ──
    this.rifts.push({ x: 34, y: 62 });
    this.rifts.push({ x: MAP_W - 35, y: MAP_H - 63 });
    for (const r of this.rifts) {
      for (let dy = -3; dy <= 3; dy++) {
        for (let dx = -3; dx <= 3; dx++) {
          if (dx * dx + dy * dy > 9) continue;
          const tx = r.x + dx;
          const ty = r.y + dy;
          if (!this.inBounds(tx, ty)) continue;
          const i = this.idx(tx, ty);
          if (this.tiles[i] === Tile.Ground) this.tiles[i] = Tile.Rift;
        }
      }
    }

    // ── 清理基地周边，保证开局有足够的建造空间 ──
    for (const b of this.bases) {
      for (let dy = -7; dy <= 7; dy++) {
        for (let dx = -7; dx <= 7; dx++) {
          const tx = b.x + dx;
          const ty = b.y + dy;
          if (!this.inBounds(tx, ty)) continue;
          const i = this.idx(tx, ty);
          if (this.tiles[i] === Tile.Rock) this.tiles[i] = Tile.Ground;
        }
      }
    }

    // ── 地图边界封死，防止单位走出可视范围 ──
    for (let i = 0; i < MAP_W; i++) {
      this.tiles[this.idx(i, 0)] = Tile.Rock;
      this.tiles[this.idx(i, MAP_H - 1)] = Tile.Rock;
      this.tiles[this.idx(0, i)] = Tile.Rock;
      this.tiles[this.idx(MAP_W - 1, i)] = Tile.Rock;
    }

    // ── 连通性保障：从基地 A 洪水填充，凿通无法抵达基地 B 的情况 ──
    this.ensureConnected(this.bases[0]!, this.bases[1]!);
  }

  fieldAt(tx: number, ty: number): number {
    if (!this.inBounds(tx, ty)) return 0;
    return this.oreField[this.idx(tx, ty)]!;
  }

  /** 把相邻矿格标成同一矿区，供拾荒车锁定产区 */
  private labelOreFields(): void {
    this.oreField.fill(0);
    const n = MAP_W * MAP_H;
    const qx = new Int16Array(n);
    const qy = new Int16Array(n);
    let fid = 0;
    const ndx = [0, 1, 0, -1, 1, 1, -1, -1];
    const ndy = [1, 0, -1, 0, 1, -1, 1, -1];
    for (let ty = 0; ty < MAP_H; ty++) {
      for (let tx = 0; tx < MAP_W; tx++) {
        const i0 = this.idx(tx, ty);
        if (this.ore[i0]! <= 0 || this.oreField[i0]! !== 0) continue;
        fid++;
        if (fid > 255) fid = 255;
        let qh = 0;
        let qt = 0;
        qx[qt] = tx;
        qy[qt] = ty;
        qt++;
        this.oreField[i0] = fid;
        while (qh < qt) {
          const cx = qx[qh]!;
          const cy = qy[qh]!;
          qh++;
          for (let k = 0; k < 8; k++) {
            const nx = cx + ndx[k]!;
            const ny = cy + ndy[k]!;
            if (!this.inBounds(nx, ny)) continue;
            const ni = this.idx(nx, ny);
            if (this.ore[ni]! <= 0 || this.oreField[ni]! !== 0) continue;
            this.oreField[ni] = fid;
            qx[qt] = nx;
            qy[qt] = ny;
            qt++;
          }
        }
      }
    }
  }

  private nearAnyBase(x: number, y: number, r: number): boolean {
    for (const b of this.bases) {
      const dx = b.x - x;
      const dy = b.y - y;
      if (dx * dx + dy * dy < r * r) return true;
    }
    return false;
  }

  private stamp(cx: number, cy: number, r: number, t: Tile): void {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (dx * dx + dy * dy > r * r) continue;
        const tx = cx + dx;
        const ty = cy + dy;
        if (!this.inBounds(tx, ty)) continue;
        if (tx < 2 || ty < 2 || tx > MAP_W - 3 || ty > MAP_H - 3) continue;
        this.tiles[this.idx(tx, ty)] = t;
      }
    }
  }

  /** 若两基地不连通，沿直线凿开一条通道 */
  private ensureConnected(a: BaseSite, b: BaseSite): void {
    const seen = new Uint8Array(MAP_W * MAP_H);
    const queue = new Int32Array(MAP_W * MAP_H);
    let head = 0;
    let tail = 0;
    const start = this.idx(a.x, a.y);
    queue[tail++] = start;
    seen[start] = 1;
    const goal = this.idx(b.x, b.y);
    let found = false;
    while (head < tail) {
      const cur = queue[head++]!;
      if (cur === goal) {
        found = true;
        break;
      }
      const cx = cur % MAP_W;
      const cy = (cur / MAP_W) | 0;
      for (let d = 0; d < 4; d++) {
        const nx = cx + (d === 0 ? 1 : d === 1 ? -1 : 0);
        const ny = cy + (d === 2 ? 1 : d === 3 ? -1 : 0);
        if (!this.passableTerrain(nx, ny)) continue;
        const ni = this.idx(nx, ny);
        if (seen[ni]) continue;
        seen[ni] = 1;
        queue[tail++] = ni;
      }
    }
    if (found) return;

    // 直线凿通，宽度 3
    let x = a.x;
    let y = a.y;
    while (x !== b.x || y !== b.y) {
      this.stamp(x, y, 1, Tile.Ground);
      if (x < b.x) x++;
      else if (x > b.x) x--;
      if (y < b.y) y++;
      else if (y > b.y) y--;
    }
  }
}
